
<template lang="pug">
  .product-carousel
    icon-button.product-carousel__prev(
      v-if="multipleImages && 'mobile' !== $mq"
      label="Next Slide",
      icon="chevron-left-big",
      size="64px"
      @click="$refs.productImageCarousel.slidePrev()"
    )
    icon-button.product-carousel__next(
      v-if="multipleImages && 'mobile' !== $mq"
      label="Previous Slide",
      icon="chevron-right-big",
      size="64px"
      @click="$refs.productImageCarousel.slideNext()"
    )
    carousel(
      ref="productImageCarousel",
      :key="currentVariantId",
      v-if="product.images",
      :slides-to-show="1",
      :infinite="true",
      :dots="true",
      :thumbnail-images="!['mobile', 'tablet'].includes($mq) ? thumbnailImages : []"
    )
      slide(v-for="(image, index) in images", :key="index")
        product-carousel-image(:image="image")
</template>

<script>
  import { mapState } from 'vuex';
  import IconButton from 'scripts/components/buttons/IconButton.vue';
  import Carousel from 'scripts/components/basic/Carousel.vue';
  import Slide from 'scripts/components/basic/Slide.vue';
  import ProductCarouselImage from 'scripts/components/product/ProductCarouselImage.vue';

  export default {
    name: 'ProductCarousel',
    components: {
      IconButton,
      Carousel,
      Slide,
      ProductCarouselImage,
    },
    props: {
      product: Object,
    },
    computed: {
      ...mapState({
        currentVariantId: state => state.productPage.currentVariantId,
      }),
      variantImageIds () {
        if (!this.product || !this.product.variants) return [];
        return this.product.variants
          .filter(v => v.image && v.image.id)
          .map(v => v.image.id);
      },
      allImages () {
        return (this.product && this.product.images) ? this.product.images : [];
      },
      images () {
        if (!this.product || !this.product.variants) {
          return this.allImages;
        }
        // Use currentVariantId from store, or fall back to first variant on page load
        const variant = this.currentVariantId
          ? this.product.variants.find(v => v.id === this.currentVariantId)
          : this.product.variants[0];
        if (!variant || !variant.image) return this.allImages;

        const currentVariantImageId = variant.image.id;
        let startIndex = this.allImages.findIndex(img => img.id === currentVariantImageId);

        // If variant image not found (e.g., first variant's image was skipped by offset:1), start at 0
        if (startIndex === -1) {
          startIndex = -1; // Will become 0 after +1
        }

        // Find end boundary: look for ANOTHER variant's assigned image (JPG)
        // Note: variantImageIds may only contain some variants' images if not all variants have assigned images
        let endIndex = this.allImages.length;
        for (let i = startIndex + 1; i < this.allImages.length; i++) {
          const imgId = this.allImages[i].id;
          // Stop when we hit another variant's image (but not current variant's)
          // Use == for loose comparison in case of string/number mismatch
          const isVariantImage = this.variantImageIds.some(vid => vid == imgId);
          if (isVariantImage && imgId != currentVariantImageId) {
            endIndex = i;
            break;
          }
        }
        return this.allImages.slice(startIndex + 1, endIndex);
      },
      thumbnailImages () {
        return this.images.map(image => {
          // Handle both string URLs (from .js API) and objects (from JSON)
          if (typeof image === 'string') {
            // If image is a string URL, create an object with src
            return {
              src: image
            };
          } else if (image && typeof image === 'object') {
            // If image is an object, ensure it has a src property
            return {
              ...image,
              src: image.src || image.url || ''
            };
          } else {
            // Fallback for unexpected types
            return {
              src: ''
            };
          }
        });
      },
      multipleImages () {
        return this.images.length > 1;
      },
    },
    watch: {
      currentVariantId (newVariantId) {
        if (!newVariantId || !this.product || !this.product.variants) return;

        const variant = this.product.variants.find(v => v.id === newVariantId);
        if (!variant || !variant.image) return;

        const imageIndex = this.images.findIndex(img => img.id === variant.image.id);
        if (imageIndex !== -1 && this.$refs.productImageCarousel) {
          this.$refs.productImageCarousel.slideTo(imageIndex);
        }
      }
    },
  };
</script>

<style scoped lang="scss">
  .product-carousel {
    position: relative;
    padding: 0 #{24px + $grid-gutter * 2};

    @include mobile-only {
      padding: 0 #{$grid-gutter * 1.5};
      max-width: 550px;
      margin-left: auto;
      margin-right: auto;
    }

    &__next,
    &__prev {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
    }

    &__next {
      right: 45px;

      @include mobile-only {
        right: 4px;
      }
    }

    &__prev {
      left: 45px;

      @include mobile-only {
        left: 4px;
      }
    }

    &__image {
      align-self: center;
    }

    &::v-deep {
      .carousel__dots {
        margin-top: $grid-gutter;
      }
    }
  }
</style>
